import { useTranslation } from 'react-i18next';
import { BURST, evLabel } from '../../utils/camera/capture';
import { ZStackSettings } from '../zstack/ZStackPanel';

/**
 * A bracket's three photos, to keep one (the dish and microscope cameras):
 * `shots` [{ev, url}], `picked` the index kept, `onPick(index)`.
 */
export function BracketPick({ shots, picked, onPick, disabled = false, testIdPrefix = 'camera' }) {
  const { t } = useTranslation('camera');
  return (
    <div role="radiogroup" aria-label={t('capture.pick')} data-testid={`${testIdPrefix}-bracket`}
         style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      {shots.map((s, i) => (
        <button key={s.ev} type="button" role="radio" aria-checked={picked === i} disabled={disabled}
                aria-label={t('capture.pickOne', { ev: evLabel(s.ev) })} data-testid={`${testIdPrefix}-bracket-${i}`}
                onClick={() => onPick(i)}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: 3, borderRadius: 6,
                         cursor: 'pointer', background: 'transparent', color: 'var(--c-text-fg)',
                         border: `2px solid ${picked === i ? 'var(--c-primary)' : 'var(--c-border)'}` }}>
          {s.url && <img src={s.url} alt="" style={{ width: 84, height: 63, objectFit: 'cover', borderRadius: 4 }} />}
          <bdi dir="ltr" style={{ fontSize: 12, fontWeight: picked === i ? 700 : 400 }}>{evLabel(s.ev)}</bdi>
        </button>
      ))}
    </div>
  );
}

/*
 * The Pro drawer's capture modes (2026-10-22; docs/FIELD_AI_VISION_SPEC.md
 * 10.2), the same in every camera: Single, Burst (3 or 5 frames, the
 * sharpest kept), Exposure bracket (-1, 0 and +1 EV) or, where the host takes
 * one, a Z-stack (`zstack`: useZStack()'s answer; its settings show when it
 * is chosen), only those this camera can do. `capture` is useCaptureMode()'s
 * answer. `children` is what a host adds below (none since the thermal
 * import left the Field camera, 2026-10-26).
 * `tone` as CameraControls'.
 */
export default function CaptureModes({ capture, zstack = null, tone = 'light', testIdPrefix = 'camera', children = null }) {
  const { t } = useTranslation('camera');
  const id = name => `${testIdPrefix}-${name}`;
  const dark = tone === 'dark';
  const muted = { margin: 0, fontSize: 12, color: dark ? 'rgba(255,255,255,0.8)' : 'var(--c-text-muted-fg)' };
  const btn = active => ({
    minHeight: 32, padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: active ? 700 : 400,
    border: `1px solid ${active ? 'var(--c-primary)' : (dark ? 'rgba(255,255,255,0.5)' : 'var(--c-border-strong)')}`,
    background: active ? (dark ? 'rgba(255,255,255,0.25)' : 'var(--c-primary-soft)') : 'transparent',
    color: dark ? '#fff' : 'var(--c-text-fg)',
  });
  const { mode, modes } = capture;
  return (
    <fieldset data-testid={id('modes')} data-mode={mode} aria-busy={capture.busy || undefined}
              style={{ border: `1px solid ${dark ? 'rgba(255,255,255,0.3)' : 'var(--c-border)'}`, borderRadius: 8,
                       padding: '4px 10px 8px', margin: 0, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0,
                       color: dark ? '#fff' : 'var(--c-text-fg)' }}>
      <legend style={{ fontSize: 13, fontWeight: 600, padding: '0 4px' }}>{t('capture.title')}</legend>
      <div role="radiogroup" aria-label={t('capture.mode')} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {modes.map(m => (
          <button key={m} type="button" role="radio" aria-checked={mode === m} data-testid={id(`mode-${m}`)}
                  disabled={capture.busy || !!zstack?.busy} onClick={() => capture.setMode(m)} style={btn(mode === m)}>
            {t(`capture.${m}`)}
          </button>
        ))}
      </div>
      {mode === 'burst' && (
        <div role="radiogroup" aria-label={t('capture.frames')} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {BURST.frames.map(n => (
            <button key={n} type="button" role="radio" aria-checked={capture.frames === n} data-testid={id(`mode-frames-${n}`)}
                    disabled={capture.busy} onClick={() => capture.setFrames(n)} style={btn(capture.frames === n)}>
              {t('capture.framesN', { count: n })}
            </button>
          ))}
        </div>
      )}
      {mode === 'zstack' && zstack && <ZStackSettings zstack={zstack} tone={tone} testIdPrefix={testIdPrefix} />}
      <p style={muted}>{t(`capture.${mode}Help`, { count: capture.frames })}</p>
      {capture.busy && <p role="status" style={muted} data-testid={id('modes-busy')}>{t('capture.busy')}</p>}
      {capture.refused && (
        <p role="alert" data-testid={id('modes-refused')} style={{ ...muted, color: dark ? '#ffcdd2' : 'var(--c-danger-fg)' }}>
          {t('capture.refused')}
        </p>
      )}
      {children}
    </fieldset>
  );
}
